import React, { ReactNode, SyntheticEvent, useContext } from 'react'
import styled from 'styled-components'
import { MAX_FILE_COUNT } from '../../../../constants'
import { CreationToolsContext } from '../../../../manager'
import CreationTool from '../../../../types/creation-tool'
import CreationToolError from '../../../../types/error'
import CreationToolGAEvent from '../../../../types/ga-event'
import CreationToolOutputType from '../../../../types/output-type'
import CreationToolWelcomeButton from '../../../../types/welcome-button'
import getSourceFrames from '../../../../utils/get-source-frames'
import getUnvalidatedSource from '../../../../utils/get-unvalidated-source'
import { validateSourceByInput } from '../../../../utils/validate-source'
import DropZone from '../../../drop-zone'
import { CreationToolsWelcomeContext } from '../../context'

const Container = styled(DropZone)`
    input[type='file'] {
        cursor: pointer;
        height: 100%;
        left: 0;
        margin: 0;
        opacity: 0;
        padding: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }
`

type Props = {
    children?: ReactNode
    className?: string
    button: CreationToolWelcomeButton
}

const CreationToolsWelcomeFileButton = ({ children, className, button }: Props) => {
    const { tool, setFiles, trackEvent, isDraggingFile } = useContext(CreationToolsContext)
    const { setError, setErrorSource } = useContext(CreationToolsWelcomeContext)
    const outputType = CreationToolWelcomeButton.outputType(button)
    const mimeTypes = CreationToolWelcomeButton.mimeTypes(button)
    const gradient = CreationTool.gradient(tool)

    const onFileInput = async (e: SyntheticEvent<HTMLInputElement, Event>) => {
        const { files } = e.currentTarget

        if (!files || !files.length) return

        // check if too many files
        if (files.length > MAX_FILE_COUNT) {
            setError(CreationToolError.TooManyFiles)
            return
        }

        if (files.length === 1) {
            const file = files[0]

            try {
                const requiresFrameProcessing = CreationTool.requiresFrameProcessing(tool)
                // if the file will be processed into an array of frames, we don't need
                // to worry about validating audio or transparency. this will automatically
                // be accounted for via the tool's creation steps. (i.e. Cut Out)
                const validatedFile = await validateSourceByInput(file, mimeTypes, {
                    requiresAudio: !requiresFrameProcessing && CreationToolOutputType.requiresAudio(outputType),
                    requiresTransparency:
                        !requiresFrameProcessing && CreationToolOutputType.requiresTransparency(outputType),
                })
                let processedFile = validatedFile

                if (requiresFrameProcessing) {
                    const frames = await getSourceFrames(validatedFile)
                    processedFile.frames = frames
                }

                setFiles([processedFile], outputType)
            } catch (error) {
                trackEvent(CreationToolGAEvent.welcomeButtonCategory(button), 'Bad Format Error', file.type)
                setError(error as CreationToolError)
                setErrorSource(file)
            }
        } else {
            const sourceFiles = Array.from(files).map(getUnvalidatedSource)
            setFiles(sourceFiles, outputType)
        }

        const eventLabel = isDraggingFile ? 'Drag And Drop' : 'Browse Files'
        trackEvent(CreationToolGAEvent.welcomeButtonCategory(button), 'Input', eventLabel)
    }

    return (
        <Container
            {...{
                className,
                gradient,
            }}
        >
            <>
                {children}
                <input
                    type="file"
                    accept={mimeTypes.join(',')}
                    multiple={CreationTool.supportsMultipleFiles(tool)}
                    onInput={onFileInput}
                />
            </>
        </Container>
    )
}

export default CreationToolsWelcomeFileButton
